import React, { useState, useMemo } from 'react';
import { Dialog } from '../../shared/components/Dialog';
import { OTPInput, Button } from '@shared/components';
import { usePhoneVerification } from '../hooks/usePhoneVerification';
import { getUser } from '@state/authSlice';
import { getCourierSetting } from '@state/courierSlice';
import { useSelector } from 'react-redux';
import { MessageSquare } from 'lucide-react';
import { useTrackEvent } from '../../utils/track';
import { trackSignupIfPending } from '../../utils/signupTracking';

interface PhoneVerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PhoneVerificationModal: React.FC<PhoneVerificationModalProps> = ({ isOpen, onClose }) => {
  const { sendPhoneVerification, isSendingVerification, verifyPhoneCode } = usePhoneVerification();
  const [otpSent, setOtpSent] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const user = useSelector(getUser);
  const courierSetting = useSelector(getCourierSetting);
  const track = useTrackEvent();
  
  // Determine verification method (prioritize SMS)
  const verificationMethod: 'sms' | 'whatsapp' = useMemo(() => {
    if (courierSetting?.isSmsApiEnabled) {
      return 'sms';
    }
    return 'whatsapp';
  }, [courierSetting]);

  const methodName = verificationMethod === 'sms' ? 'SMS' : 'WhatsApp';

  const handleSendVerification = async () => {
    try {
      await sendPhoneVerification(user?.id?.toString() || '', verificationMethod);
      setOtpSent(true);
    } catch (error) {
      console.error('Failed to send verification code:', error);
    }
  };

  const handleResendCode = async () => {
    setIsResending(true);
    try {
      await sendPhoneVerification(user?.id?.toString() || '', verificationMethod);
    } catch (error) {
      console.error('Failed to resend verification code:', error);
    } finally {
      setIsResending(false);
    }
  };

  const handleVerifyCode = async (code: string) => {
    const result = await verifyPhoneCode(code, user?.id?.toString() || '', verificationMethod);
    if (result.success) {
      // Email + phone now both verified. If this completes a new signup, count it.
      trackSignupIfPending(user?.id?.toString() || '', track);
      onClose();
    }
  };

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title="Verify Your Phone Number">
      <div className="p-6">
        <div className="flex flex-col items-center space-y-6">
          {/* Phone Number Display */}
          <div className="flex items-center justify-center space-x-2 text-lg font-medium text-gray-900 dark:text-gray-100">
            <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56-.35-.12-.74-.03-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z"/>
            </svg>
            <span>{user?.contactInformation?.mobilePhone || 'No phone number available'}</span>
          </div>

          {/* Progress Steps */}
          <div className="w-full max-w-sm">
            <div className="flex items-center justify-between mb-2">
              <div className={`flex items-center ${otpSent ? 'text-green-600 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'}`}>
                <div className="flex items-center justify-center w-8 h-8 rounded-full border-2 border-current">
                  {otpSent ? (
                    <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                    </svg>
                  ) : <span>1</span>}
                </div>
                <span className="ml-2">Send Code</span>
              </div>
              <div className={`flex items-center ${otpSent ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'}`}>
                <div className="flex items-center justify-center w-8 h-8 rounded-full border-2 border-current">
                  <span>2</span>
                </div>
                <span className="ml-2">Verify</span>
              </div>
            </div>
          </div>

          {!otpSent ? (
            <div className="w-full space-y-6">
              <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                <div className="flex items-center space-x-3">
                  {verificationMethod === 'sms' ? (
                    <MessageSquare className="w-6 h-6 text-green-600 dark:text-green-400" />
                  ) : (
                    <svg className="w-6 h-6 text-green-600 dark:text-green-400" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                  )}
                  <p className="text-green-800 dark:text-green-200">
                    Verify your phone number via {methodName} to earn points and receive important updates!
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <Button
                  onClick={handleSendVerification}
                  disabled={isSendingVerification}
                  isLoading={isSendingVerification}
                  variant="primary"
                  fullWidth
                  className="flex items-center justify-center space-x-2"
                >
                  {verificationMethod === 'sms' ? (
                    <MessageSquare className="w-5 h-5" />
                  ) : (
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347"/>
                    </svg>
                  )}
                  <span>Start {methodName} Verification</span>
                </Button>
                <Button
                  onClick={onClose}
                  variant="outline"
                  fullWidth
                >
                  Verify Later
                </Button>
              </div>
            </div>
          ) : (
            <div className="w-full">
              <OTPInput
                onComplete={handleVerifyCode}
                onResend={handleResendCode}
                isResending={isResending}
                title="Enter Verification Code"
                description={`Please enter the verification code sent to your phone via ${methodName}`}
              />
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default PhoneVerificationModal;
